import React from 'react';
import { ArrowUpRight, ClipboardList, PencilLine, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { getFormIdentity } from './requestsUtils';

export const FormCard = ({ form, projectId, onDelete, onEdit }) => {
  const { t, formatNumber } = useI18n();
  const navigate = useNavigate();
  const identity = getFormIdentity(form);
  const title = form.displayName || form.DisplayName || identity.slug || t('requestsUntitledForm');
  const count = Number(form.submissionCount ?? form.SubmissionCount) || 0;

  const openSubmissions = () => {
    if (!identity.id) {
      return;
    }

    navigate(`/workspace/${projectId}/requests/${identity.id}`, {
      state: { form }
    });
  };

  return (
    <article className="relative flex min-h-[176px] flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white transition hover:border-neutral-300 hover:bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700 dark:hover:bg-neutral-900/50">
      <div className="relative flex flex-1 flex-col p-4">
        <button
          type="button"
          className="group flex flex-1 items-start gap-3 text-start"
          onClick={openSubmissions}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
            <ClipboardList size={18} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <h3 className="line-clamp-2 flex-1 text-sm font-semibold tracking-tight text-neutral-950 dark:text-white">
                {title}
              </h3>
              <ArrowUpRight
                size={15}
                className="mt-0.5 shrink-0 text-neutral-300 opacity-0 transition group-hover:opacity-100 dark:text-neutral-600"
              />
            </div>
            {identity.slug ? (
              <p className="mt-1.5 truncate text-[11px] text-neutral-400" dir="ltr">
                {identity.slug}
              </p>
            ) : null}
            <p className="mt-1.5 text-[11px] text-neutral-400">
              {form.hasSchema ? t('requestsHasSchema') : t('requestsNoSchema')}
            </p>
          </div>
        </button>

        <div className="mt-auto flex items-end justify-between gap-3 border-t border-neutral-100 pt-3 dark:border-neutral-900">
          <button type="button" className="text-start" onClick={openSubmissions}>
            <p className="text-[11px] font-medium text-neutral-400">{t('requestsSubmissionCount')}</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-neutral-950 dark:text-white">
              {formatNumber(count)}
            </p>
          </button>

          <div className="flex items-center gap-1">
            <button
              aria-label={t('requestsEditForm')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(form);
              }}
              type="button"
            >
              <PencilLine size={15} />
            </button>
            <button
              aria-label={t('requestsDeleteForm')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(form);
              }}
              type="button"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};
